<template>
  <div
    class="orders-list"
    data-cy="order-list"
  >
    <div
      v-if="!statusPending"
      class="list-group"
      data-cy="order-list-items"
    >
      <div
        v-for="order in sortedOrders"
        :key="order.id"
        class="list-group-item"
        data-cy="order-list-item"
        @click="orderSelected(order)"
      >
        <div
          class="orders-list__order-status"
          :class="{ pending: !order.completed, canceled: isOrderCanceled(order) }"
        >
          <i
            class="fa"
            :class="orderIcon(order)"
          />
        </div>
        <div class="orders-list__order-summary">
          <div class="orders-list__order-heading">
            <h2 class="orders-list__order-title">
              {{ orderItemTitle(order) }}
            </h2>
            <span class="orders-list__order-timestamp">{{
              getDate(order.startTime)
            }}</span>
          </div>

          <p
            class="orders-list__order-preview"
            data-cy="order-list-item-status"
          >
            {{ orderStatus(order) }}
          </p>
        </div>
      </div>
      <div
        v-if="sortedOrders.length == 0"
        v-t="'NO_ORDERS_FOUND'"
        class="orders-list__not-found"
      />
    </div>
    <div
      v-if="statusPending"
      class="p-t-10"
    >
      <Loader />
      <span v-t="'ORDERS_LOADING'" />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Loader from '@/core/components/Loader';
import { STATUS_PENDING } from '@/store/helpers';
import messages from '../../locales';

export default {
  components: { Loader },
  props: {
    orders: {
      type: Array,
      default() {
        return [];
      },
    },
    status: {
      type: Object,
      default() {
        return {};
      },
    },
    timezone: {
      type: String,
      default: 'America/Los_Angeles',
    },
  },
  computed: {
    statusPending() {
      return this.status.status == STATUS_PENDING;
    },
    sortedOrders() {
      // clone the array and sort the clone
      return [...this.orders].sort((a, b) => b.startTime - a.startTime);
    },
  },
  methods: {
    orderSelected(order) {
      this.$emit('selected', order);
    },
    getDate(timeStamp) {
      return moment(timeStamp).tz(this.timezone).format('MMM DD hh:mm:ss A');
    },
    orderStatus(order) {
      if (!order.completed) {
        return 'Pending';
      }
      if (this.isOrderCanceled(order)) {
        return 'Canceled';
      }
      return 'Completed';
    },
    orderIcon(order) {
      if (!order.completed) {
        return 'fa-clock-o';
      }
      if (this.isOrderCanceled(order)) {
        return 'fa-ban';
      }
      return 'fa-check';
    },
    isOrderCanceled(order) {
      return order.canceledByUser || (order.completed && order.errorCode >= 0);
    },
    orderItemTitle(order) {
      const translateValues = {
        completed: order.completedAmount,
        requested: order.requestAmount,
        countryCode: order.assigmentSettings.countryCode,
        numberType: order.assigmentSettings.isTollFree ? 'TF' : '',
        areaCode: order.assigmentSettings.areaCode,
      };
      return this.$t('ORDER_ITEM_TITLE', translateValues);
    },
  },
  i18n: {
    messages,
    locale: 'en',
  },
};
</script>

<style lang="scss" scoped>
.orders-list {
  display: flex;
  justify-content: center;
  .list-group {
    flex: 1;
    margin: 0;
    max-height: 40.5vh;
    overflow-y: auto;

    .list-group-item {
      display: flex;
      overflow: hidden;
      min-width: 0;
      align-content: center;
      padding: 5px 12px 5px 0;
      background-color: var(
        --component-in-app-notifications__list-group-item-bg
      );
      border-color: var(
        --component-in-app-notifications__list-group-item-border-color
      );
      border-right: hidden;
      border-left: hidden;
      cursor: pointer;

      &:hover {
        background-color: var(
          --component-in-app-notifications__list-group-item--hover-bg
        );
      }

      .orders-list__order-status {
        border: 1px solid
          var(
            --component-in-app-notifications__list-group-item--read-border-color
          );
        border-width: 0 3px 0 0;
        margin-right: 10px;
        padding-left: 10px;
        padding-right: 10px;
        &.pending {
          border-color: var(
            --component-in-app-notifications__list-group-item--unread-border-color
          );
        }
        &.canceled {
          border-color: var(--component-changelog__entry--delete-border-color);
        }
        i {
          font-size: 0.95em;
          padding: 0;
          min-width: 13px;
        }
      }
      .orders-list__order-summary {
        overflow: hidden;
        flex-grow: 1;
        .orders-list__order-heading {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 5px;
          min-width: 0;
          color: var(
            --component-in-app-notifications__list-group-item-header-font-color
          );

          .orders-list__order-title {
            font-size: 14px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .orders-list__order-timestamp {
            color: var(
              --component-in-app-notifications__summary-timestamp-font-color
            );
            font-weight: 300;
            line-height: 20px;
            white-space: nowrap;
            font-size: 10px;
            margin-left: 15px;
            justify-self: end;
          }
        }

        .orders-list__order-preview {
          position: relative;
          color: var(
            --component-in-app-notifications__summary-preview-font-color
          );
          line-height: 20px;
          max-height: 60px; // 3 lines
          overflow: hidden;
          &:after {
            content: '';
            position: absolute;
            bottom: 0;
            right: 0;
            width: 50%;
            height: 20px;
            background: linear-gradient(
              to right,
              rgba($white, 0),
              var(
                  --component-in-app-notifications__message-preview-text-overflow
                )
                100%
            );
          }
          @supports (-webkit-line-clamp: 3) {
            text-overflow: ellipsis;
            display: -webkit-box;
            /* autoprefixer: off */
            -webkit-box-orient: vertical;
            /* autoprefixer: on */
            -webkit-line-clamp: 3;
            &:after {
              display: none;
            }
          }
        }
      }

      &.show-more {
        justify-content: center;
        padding: 10px;
        color: var(--component-in-app-show-more-font-color);
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  .orders-list__not-found {
    padding: 20px;
    display: inline-block;
    vertical-align: middle;
    text-align: center;
  }
}
</style>